import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const TABS = [
  { id: "stats",       label: "Vue d'ensemble" },
  { id: "users",       label: "Utilisateurs" },
  { id: "pass",        label: "Pass 24h" },
  { id: "feedback",    label: "Avis" },
  { id: "leaderboard", label: "Leaderboard" },
  { id: "emails",      label: "Emails" },
];

function Stars({ n }) {
  return [1, 2, 3, 4, 5].map((s) => (
    <span key={s} style={{ color: s <= n ? "var(--gold)" : "var(--muted)", fontSize: '0.8125rem' }}>★</span>
  ));
}

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value ?? "—"}</div>
      <div className="admin-stat-label">{label}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

function BarChart({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="admin-chart">
      {data.map((d, i) => (
        <div key={i} className="admin-chart-col">
          <div className="admin-chart-bar-wrap">
            <div
              className="admin-chart-bar"
              style={{ height: `${d.count > 0 ? Math.max((d.count / max) * 100, 6) : 0}%` }}
              title={`${d.count} rapport${d.count > 1 ? "s" : ""}`}
            />
          </div>
          <span className="admin-chart-val">{d.count > 0 ? d.count : ""}</span>
          <span className="admin-chart-label">{d.date.slice(5).replace("-", "/")}</span>
        </div>
      ))}
    </div>
  );
}

function UserDetail({ userId, token, onBack, onApprove, approving }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => setDetail(res.data)).finally(() => setLoading(false));
  }, [userId, token]);

  if (loading) return <p style={{ color: "var(--muted)", textAlign: "center", padding: "1.5rem 0" }}>Chargement...</p>;
  if (!detail) return <p style={{ color: "#ff8080", textAlign: "center" }}>Erreur de chargement.</p>;

  return (
    <div>
      <button className="admin-back-btn" onClick={onBack}>← Retour</button>

      <div className="admin-user-detail-header">
        <div>
          <div className="admin-user-detail-name">{detail.firstname || "—"}</div>
          <div className="admin-user-email">{detail.email}</div>
        </div>
        <div className="admin-user-detail-tags">
          {detail.is_subscribed && <span className="admin-tag admin-tag--premium">Premium</span>}
          {detail.is_admin && <span className="admin-tag admin-tag--admin">Admin</span>}
          {!detail.is_verified && <span className="admin-tag admin-tag--muted">Non vérifié</span>}
        </div>
      </div>

      <div className="admin-user-detail-grid">
        <div className="admin-detail-item">
          <span className="admin-detail-label">Inscrit le</span>
          <span className="admin-detail-value">{detail.created_at ? detail.created_at.slice(0, 10) : "—"}</span>
        </div>
        <div className="admin-detail-item">
          <span className="admin-detail-label">Recherches aujourd'hui</span>
          <span className="admin-detail-value">{detail.searches_today}</span>
        </div>
        <div className="admin-detail-item">
          <span className="admin-detail-label">Dernière recherche</span>
          <span className="admin-detail-value">{detail.last_search_date || "—"}</span>
        </div>
        <div className="admin-detail-item">
          <span className="admin-detail-label">Rapports sauvegardés</span>
          <span className="admin-detail-value">{detail.reports.length}</span>
        </div>
      </div>

      {!detail.is_subscribed && (
        <button
          className="admin-approve-btn"
          style={{ width: "100%", marginBottom: '1rem', padding: "0.625rem" }}
          onClick={() => onApprove(detail.id)}
          disabled={approving === detail.id}
        >
          {approving === detail.id
            ? "..."
            : detail.has_pending_premium
            ? "✓ Approuver la demande Premium"
            : "✓ Faire passer Premium"}
        </button>
      )}

      <h3 className="admin-section-title" style={{ marginTop: '0.25rem' }}>
        Historique des rapports
      </h3>

      {detail.reports.length === 0 ? (
        <p className="admin-empty">Aucun rapport sauvegardé.</p>
      ) : (
        <div className="admin-list">
          {detail.reports.map((r, i) => (
            <div key={i} className="admin-report-row">
              <div className="admin-report-left">
                <span className="admin-report-player">{r.player}</span>
                {r.badge && <span className="admin-report-badge">{r.badge}</span>}
              </div>
              <div className="admin-report-right">
                <span className="admin-report-score">{r.score}/100</span>
                {r.created_at && (
                  <span className="admin-user-meta">{r.created_at.slice(0, 10)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SIGNATURES = {
  fr: {
    text: `\n\n—\nL'équipe Roast My Stats\ncontact@roast-my-stats.com\nhttps://roast-my-stats.com`,
    html: `<br><br><hr style="border:none;border-top:1px solid #2d2d4e;margin:20px 0;">\n<p style="color:#475569;font-size:13px;margin:0;">L'équipe <strong style="color:#a78bfa;">Roast My Stats</strong><br>\n<a href="mailto:contact@roast-my-stats.com" style="color:#7c3aed;text-decoration:none;">contact@roast-my-stats.com</a> ·\n<a href="https://roast-my-stats.com" style="color:#7c3aed;text-decoration:none;">roast-my-stats.com</a></p>`,
  },
  en: {
    text: `\n\n—\nThe Roast My Stats Team\ncontact@roast-my-stats.com\nhttps://roast-my-stats.com`,
    html: `<br><br><hr style="border:none;border-top:1px solid #2d2d4e;margin:20px 0;">\n<p style="color:#475569;font-size:13px;margin:0;">The <strong style="color:#a78bfa;">Roast My Stats</strong> Team<br>\n<a href="mailto:contact@roast-my-stats.com" style="color:#7c3aed;text-decoration:none;">contact@roast-my-stats.com</a> ·\n<a href="https://roast-my-stats.com" style="color:#7c3aed;text-decoration:none;">roast-my-stats.com</a></p>`,
  },
};

function EmailComposer({ users, token }) {
  const [filter, setFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [useHtml, setUseHtml] = useState(false);
  const [lang, setLang] = useState("fr");
  const [signature, setSignature] = useState(SIGNATURES.fr.text);
  const [signatureHtml, setSignatureHtml] = useState(SIGNATURES.fr.html);
  const [showSig, setShowSig] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setSignature(SIGNATURES[lang].text);
    setSignatureHtml(SIGNATURES[lang].html);
  }, [lang]);

  const filtered = users.filter((u) => {
    if (!u.is_verified) return false;
    if (planFilter === "premium" && !u.is_subscribed) return false;
    if (planFilter === "free" && u.is_subscribed) return false;
    const q = filter.toLowerCase();
    if (q && !u.email.toLowerCase().includes(q) && !(u.firstname || "").toLowerCase().includes(q)) return false;
    return true;
  });

  const allSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => { const next = new Set(prev); filtered.forEach((u) => next.delete(u.id)); return next; });
    } else {
      setSelected((prev) => { const next = new Set(prev); filtered.forEach((u) => next.add(u.id)); return next; });
    }
  };

  const toggle = (id) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const handleSend = async () => {
    const finalText = useHtml ? "" : (bodyText.trim() + signature);
    const finalHtml = useHtml ? (bodyHtml.trim() + signatureHtml) : "";
    if (!selected.size || !subject.trim() || (!finalText.trim() && !finalHtml.trim())) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/send-custom-email`,
        { user_ids: Array.from(selected), subject: subject.trim(), body_text: finalText, body_html: finalHtml },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult({ ok: true, sent: res.data.sent, errors: res.data.errors });
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || "Erreur lors de l'envoi." });
    }
    setSending(false);
  };

  const canSend = selected.size > 0 && subject.trim() && (bodyText.trim() || bodyHtml.trim()) && !sending;

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: "flex", gap: '0.5rem', marginBottom: '0.625rem', flexWrap: "wrap" }}>
        <input
          className="admin-filter-input"
          style={{ flex: 1, minWidth: '10rem', marginBottom: 0 }}
          placeholder="Filtrer par nom ou email..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {[["all", "Tous"], ["premium", "Premium"], ["free", "Free"]].map(([val, label]) => (
          <button key={val} onClick={() => setPlanFilter(val)}
            style={{ padding: "0.25rem 0.75rem", borderRadius: '1rem', fontSize: '0.75rem', border: "1px solid", cursor: "pointer", background: planFilter === val ? "var(--gold)" : "transparent", color: planFilter === val ? "#0d1b2a" : "var(--muted)", borderColor: planFilter === val ? "var(--gold)" : "rgba(255,255,255,0.1)", fontWeight: planFilter === val ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Liste utilisateurs */}
      <div style={{ maxHeight: '12.5rem', overflowY: "auto", border: "1px solid rgba(255,255,255,0.07)", borderRadius: '0.5rem', marginBottom: '0.875rem' }}>
        <div className="admin-list-row" style={{ cursor: "pointer", padding: "0.5rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.03)" }} onClick={toggleAll}>
          <input type="checkbox" checked={allSelected} readOnly style={{ marginRight: '0.625rem', accentColor: "var(--gold)" }} />
          <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>
            {selected.size} sélectionné{selected.size > 1 ? "s" : ""} · {filtered.length} affiché{filtered.length > 1 ? "s" : ""}
          </span>
        </div>
        {filtered.length === 0 ? (
          <p className="admin-empty" style={{ margin: "0.75rem 0" }}>Aucun utilisateur.</p>
        ) : filtered.map((u) => (
          <div key={u.id} className="admin-list-row"
            style={{ cursor: "pointer", padding: "0.4375rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.04)", background: selected.has(u.id) ? "rgba(124,58,237,0.07)" : "transparent" }}
            onClick={() => toggle(u.id)}>
            <input type="checkbox" checked={selected.has(u.id)} readOnly style={{ marginRight: '0.625rem', accentColor: "var(--gold)" }} />
            <div style={{ flex: 1 }}>
              <span className="admin-user-name" style={{ fontSize: '0.8125rem' }}>{u.firstname || "—"}</span>
              <span className="admin-user-email" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>{u.email}</span>
            </div>
            {u.is_subscribed && <span className="admin-tag admin-tag--premium" style={{ fontSize: '0.6875rem' }}>Premium</span>}
          </div>
        ))}
      </div>

      {/* Composer */}
      <div style={{ display: "flex", flexDirection: "column", gap: '0.625rem' }}>
        <input className="admin-filter-input" style={{ marginBottom: 0 }} placeholder="Sujet de l'email..."
          value={subject} onChange={(e) => setSubject(e.target.value)} />

        <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', flexWrap: "wrap" }}>
          <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>Corps :</span>
          {[false, true].map((html) => (
            <button key={String(html)} onClick={() => setUseHtml(html)}
              style={{ padding: "0.1875rem 0.625rem", borderRadius: '0.75rem', fontSize: '0.6875rem', border: "1px solid", cursor: "pointer", background: useHtml === html ? "rgba(124,58,237,0.3)" : "transparent", color: useHtml === html ? "var(--admin-violet)" : "var(--muted)", borderColor: useHtml === html ? "#7c3aed" : "rgba(255,255,255,0.1)" }}>
              {html ? "HTML" : "Texte"}
            </button>
          ))}
          <span style={{ fontSize: '0.75rem', color: "var(--muted)", marginLeft: '0.5rem' }}>Langue :</span>
          {[["fr", "🇫🇷 FR"], ["en", "🇬🇧 EN"]].map(([val, label]) => (
            <button key={val} onClick={() => setLang(val)}
              style={{ padding: "0.1875rem 0.625rem", borderRadius: '0.75rem', fontSize: '0.6875rem', border: "1px solid", cursor: "pointer", background: lang === val ? "rgba(124,58,237,0.3)" : "transparent", color: lang === val ? "var(--admin-violet)" : "var(--muted)", borderColor: lang === val ? "#7c3aed" : "rgba(255,255,255,0.1)" }}>
              {label}
            </button>
          ))}
        </div>

        {useHtml ? (
          <textarea style={{ background: "#0f0f1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: '0.5rem', color: "#e2e8f0", padding: "0.625rem 0.75rem", fontSize: '0.8125rem', fontFamily: "monospace", resize: "vertical", minHeight: '6.875rem' }}
            placeholder="<p>Bonjour,</p>..." value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} />
        ) : (
          <textarea style={{ background: "#0f0f1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: '0.5rem', color: "#e2e8f0", padding: "0.625rem 0.75rem", fontSize: '0.8125rem', resize: "vertical", minHeight: '6.875rem' }}
            placeholder="Bonjour,&#10;&#10;..." value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
        )}

        {/* Signature */}
        <div style={{ border: "1px solid rgba(255,255,255,0.07)", borderRadius: '0.5rem', overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0.75rem", background: "rgba(255,255,255,0.03)", cursor: "pointer" }} onClick={() => setShowSig(!showSig)}>
            <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>Signature</span>
            <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>{showSig ? "▲" : "▼"}</span>
          </div>
          {showSig && (
            useHtml ? (
              <textarea style={{ width: "100%", boxSizing: "border-box", background: "#0a0a14", border: "none", borderTop: "1px solid rgba(255,255,255,0.06)", color: "#94a3b8", padding: "0.625rem 0.75rem", fontSize: '0.75rem', fontFamily: "monospace", resize: "vertical", minHeight: '5rem' }}
                value={signatureHtml} onChange={(e) => setSignatureHtml(e.target.value)} />
            ) : (
              <textarea style={{ width: "100%", boxSizing: "border-box", background: "#0a0a14", border: "none", borderTop: "1px solid rgba(255,255,255,0.06)", color: "#94a3b8", padding: "0.625rem 0.75rem", fontSize: '0.75rem', resize: "vertical", minHeight: '4.375rem' }}
                value={signature} onChange={(e) => setSignature(e.target.value)} />
            )
          )}
        </div>

        <button className="admin-approve-btn"
          style={{ alignSelf: "flex-end", padding: "0.5625rem 1.5rem", fontSize: '0.875rem', opacity: canSend ? 1 : 0.4 }}
          onClick={handleSend} disabled={!canSend}>
          {sending ? "Envoi..." : `Envoyer à ${selected.size} destinataire${selected.size > 1 ? "s" : ""}`}
        </button>

        {result && (
          <div style={{ padding: "0.625rem 0.875rem", borderRadius: '0.5rem', background: result.ok ? "rgba(76,175,80,0.1)" : "rgba(255,80,80,0.1)", border: `1px solid ${result.ok ? "rgba(76,175,80,0.3)" : "rgba(255,80,80,0.3)"}`, fontSize: '0.8125rem' }}>
            {result.ok ? (
              <>
                <span style={{ color: "#4caf50", fontWeight: 700 }}>✓ {result.sent} email{result.sent > 1 ? "s" : ""} envoyé{result.sent > 1 ? "s" : ""}</span>
                {result.errors.length > 0 && <span style={{ color: "#ff8080", marginLeft: '0.5rem' }}>· {result.errors.length} erreur{result.errors.length > 1 ? "s" : ""}</span>}
              </>
            ) : (
              <span style={{ color: "#ff8080" }}>✗ {result.message}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InboxView({ token, onChange }) {
  const [emails, setEmails] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [erreur, setErreur] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      setEmails(res.data);
      setErreur(null);
      if (onChange) onChange();
    } catch (e) {
      // Une erreur affichee comme "Aucun message recu" ferait croire a une boite
      // vide alors que les messages sont en base : c'est la panne muette de juillet.
      setEmails([]);
      setErreur(e.response ? `Erreur ${e.response.status} : ${e.response.data?.detail || "chargement impossible"}` : "Backend injoignable.");
    }
  }, [token, onChange]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await axios.put(`${API}/admin/inbox/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setEmails((prev) => prev.map((e) => e.id === id ? { ...e, is_read: true } : e));
    if (onChange) onChange();
  };

  const handleOpen = (email) => {
    setSelected(email);
    if (!email.is_read) markRead(email.id);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    setDeleting(id);
    await axios.delete(`${API}/admin/inbox/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setEmails((prev) => prev.filter((em) => em.id !== id));
    if (selected?.id === id) setSelected(null);
    if (onChange) onChange();
    setDeleting(null);
  };

  const unread = emails ? emails.filter((e) => !e.is_read).length : 0;

  if (emails === null) return <p style={{ color: "var(--muted)", textAlign: "center", padding: "1.5rem 0" }}>Chargement...</p>;

  if (selected) {
    return (
      <div>
        <button className="admin-back-btn" onClick={() => setSelected(null)}>← Retour</button>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: "var(--text)", marginBottom: '0.375rem' }}>{selected.subject || "(Sans sujet)"}</div>
          <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', flexWrap: "wrap" }}>
            <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>
              De : <strong style={{ color: "var(--text)" }}>{selected.from_name || selected.from_email}</strong>
              {selected.from_name && <span style={{ marginLeft: '0.25rem' }}>&lt;{selected.from_email}&gt;</span>}
            </span>
            {selected.is_premium && <span className="admin-tag admin-tag--premium" style={{ fontSize: '0.625rem' }}>Premium</span>}
            <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>{selected.created_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
        </div>
        <div style={{ background: "#0f0f1a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: '0.5rem', padding: "1rem" }}>
          {selected.body_html ? (
            <iframe
              srcDoc={selected.body_html}
              style={{ width: "100%", minHeight: '18.75rem', border: "none", borderRadius: '0.25rem', background: "#fff" }}
              title="email-content"
              // sandbox vide = tout interdit : ni script, ni formulaire, ni navigation.
              // "allow-same-origin" donnait a un HTML venu du webhook l'origine du
              // dashboard ; inoffensif tant qu'"allow-scripts" est absent, fatal le
              // jour ou quelqu'un l'ajoute. Un corps d'email n'a besoin d'aucun droit.
              sandbox=""
              referrerPolicy="no-referrer"
            />
          ) : (
            <pre style={{ color: "#e2e8f0", fontSize: '0.8125rem', whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{selected.body_text || "(Corps vide)"}</pre>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.8125rem', color: "var(--muted)" }}>
          {emails.length} message{emails.length > 1 ? "s" : ""}
          {unread > 0 && <span style={{ marginLeft: '0.5rem', background: "#7c3aed", color: "#fff", fontSize: '0.6875rem', padding: "2px 0.4375rem", borderRadius: '0.625rem', fontWeight: 700 }}>{unread} non lu{unread > 1 ? "s" : ""}</span>}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: '0.5rem' }}>
          <BoutonNotifications />
          <button className="admin-refresh-btn" onClick={load} style={{ fontSize: '0.75rem' }}>↻ Actualiser</button>
        </div>
      </div>
      {erreur ? (
        <p className="admin-empty" style={{ color: "#f87171" }}>{erreur}</p>
      ) : emails.length === 0 ? (
        <p className="admin-empty">Aucun message reçu.</p>
      ) : (
        <div className="admin-list">
          {emails.map((email) => (
            <div key={email.id} className="admin-list-row"
              style={{ cursor: "pointer", padding: "0.625rem 0.75rem", background: email.is_read ? "transparent" : "rgba(124,58,237,0.06)", borderLeft: email.is_read ? "none" : "3px solid #7c3aed" }}
              onClick={() => handleOpen(email)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', marginBottom: 2, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: email.is_read ? 400 : 700, fontSize: '0.8125rem', color: "var(--text)" }}>
                    {email.from_name || email.from_email}
                  </span>
                  {email.from_name && <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>{email.from_email}</span>}
                  {email.is_premium && <span className="admin-tag admin-tag--premium" style={{ fontSize: '0.625rem' }}>Premium</span>}
                  {!email.is_read && <span style={{ width: '0.4375rem', height: '0.4375rem', borderRadius: "50%", background: "#7c3aed", flexShrink: 0 }} />}
                </div>
                <div style={{ fontSize: '0.75rem', color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {email.subject || "(Sans sujet)"} — {(email.body_text || "").slice(0, 60)}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', flexShrink: 0, marginLeft: '0.625rem' }}>
                <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>{email.created_at?.slice(0, 10)}</span>
                <button className="admin-approve-btn"
                  style={{ background: "rgba(255,80,80,0.1)", color: "#ff8080", borderColor: "rgba(255,80,80,0.3)", padding: "0.1875rem 0.5rem", fontSize: '0.75rem' }}
                  onClick={(e) => handleDelete(email.id, e)} disabled={deleting === email.id}>
                  {deleting === email.id ? "..." : "✕"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// La permission ne peut se demander que sur un clic (Safari refuse sinon) : d'ou
// un bouton plutot qu'une demande au chargement.
function BoutonNotifications() {
  const support = typeof Notification !== "undefined";
  const [permission, setPermission] = useState(support ? Notification.permission : "unsupported");
  if (permission === "granted" || permission === "unsupported") return null;
  if (permission === "denied") {
    return <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>Notifications bloquées dans les réglages du navigateur</span>;
  }
  const demander = async () => {
    try { setPermission(await Notification.requestPermission()); } catch { setPermission("denied"); }
  };
  return <button className="admin-refresh-btn" onClick={demander} style={{ fontSize: '0.75rem' }}>🔔 Activer les notifications</button>;
}

function EmailsTab({ users, token, unread, onInboxChange }) {
  // Des messages attendent : on ouvre sur la boite plutot que sur le formulaire d'envoi.
  const [view, setView] = useState(unread > 0 ? "inbox" : "compose");
  return (
    <div className="admin-section" style={{ marginBottom: 0 }}>
      <div style={{ display: "flex", gap: '0.375rem', marginBottom: '1rem' }}>
        {[["compose", "Nouveau message"], ["inbox", "Boîte de réception"]].map(([val, label]) => (
          <button key={val} onClick={() => setView(val)}
            style={{ padding: "0.375rem 1rem", borderRadius: '1.25rem', fontSize: '0.8125rem', border: "1px solid", cursor: "pointer", fontWeight: view === val ? 700 : 400, background: view === val ? "rgba(124,58,237,0.25)" : "transparent", color: view === val ? "var(--admin-violet)" : "var(--muted)", borderColor: view === val ? "#7c3aed" : "rgba(255,255,255,0.1)" }}>
            {label}
            {val === "inbox" && unread > 0 && <span className="admin-tab-badge admin-tab-badge--alerte">{unread}</span>}
          </button>
        ))}
      </div>
      {view === "compose" ? <EmailComposer users={users} token={token} /> : <InboxView token={token} onChange={onInboxChange} />}
    </div>
  );
}

export default function AdminDashboard({ token, onClose, unread = 0, onInboxChange }) {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userFilter, setUserFilter] = useState("");
  const [lbPlayers, setLbPlayers] = useState(null);
  const [lbInput, setLbInput] = useState("");
  const [lbWorking, setLbWorking] = useState(false);
  const [lbAutoAdd, setLbAutoAdd] = useState(false);
  // Les clics sur le Pass vivent dans leur propre endpoint : /admin/stats en
  // donne les totaux, le detail ligne a ligne se charge avec l'onglet.
  const [intentions, setIntentions] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
      setLastRefresh(new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }));
      setError("");
      axios.get(`${API}/admin/pass-intents`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setIntentions(r.data.intentions || []))
        .catch(() => setIntentions([]));
    } catch {
      setError("Impossible de charger les stats.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (tab === "leaderboard" && lbPlayers === null) {
      axios.get(`${API}/admin/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setLbPlayers(res.data))
        .catch(() => setLbPlayers([]));
      axios.get(`${API}/admin/leaderboard/settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setLbAutoAdd(res.data.auto_add))
        .catch(() => {});
    }
  }, [tab, token, lbPlayers]);

  const handleLbToggleAutoAdd = async () => {
    const next = !lbAutoAdd;
    setLbAutoAdd(next);
    try {
      await axios.put(`${API}/admin/leaderboard/settings`, { auto_add: next }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { setLbAutoAdd(!next); }
  };

  const handleLbAdd = async () => {
    const p = lbInput.trim();
    if (!p) return;
    setLbWorking(true);
    try {
      await axios.post(`${API}/admin/leaderboard`, { player: p }, { headers: { Authorization: `Bearer ${token}` } });
      setLbPlayers(prev => [...(prev || []), { player: p }]);
      setLbInput("");
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur.");
    }
    setLbWorking(false);
  };

  const handleLbRemove = async (player) => {
    setLbWorking(true);
    try {
      await axios.delete(`${API}/admin/leaderboard`, { params: { player }, headers: { Authorization: `Bearer ${token}` } });
      setLbPlayers(prev => prev.filter(p => p.player !== player));
    } catch {}
    setLbWorking(false);
  };

  const handleApprove = async (userId) => {
    setApproving(userId);
    try {
      await axios.post(`${API}/admin/approve/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedUserId(null);
      await fetchStats();
    } catch {
      setError("Erreur lors de l'approbation.");
    }
    setApproving(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box admin-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="admin-header">
          <h2 className="modal-title" style={{ margin: 0 }}>Dashboard Admin</h2>
          <button className="admin-refresh-btn" onClick={fetchStats} disabled={loading}>
            <span className="admin-refresh-icon">{loading ? "·" : "↻"}</span>
            {lastRefresh && <span className="admin-refresh-time">{lastRefresh}</span>}
          </button>
        </div>

        <div className="modal-tabs" style={{ marginBottom: '1.125rem' }}>
          {TABS.map((t) => {
            let badge = null;
            if (t.id === "users" && stats) badge = stats.overview.total_users;
            if (t.id === "pass" && stats && stats.pass && stats.pass.clics > 0) badge = stats.pass.clics;
            if (t.id === "feedback" && stats && stats.overview.feedback_count > 0) badge = stats.overview.feedback_count;
            const alerte = t.id === "emails" && unread > 0;
            if (alerte) badge = unread;
            return (
              <button
                key={t.id}
                className={`modal-tab${tab === t.id ? " active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {badge != null && <span className={`admin-tab-badge${alerte ? " admin-tab-badge--alerte" : ""}`}>{badge}</span>}
              </button>
            );
          })}
        </div>

        {error && <p style={{ color: "#ff8080", textAlign: "center", marginBottom: '0.75rem' }}>{error}</p>}
        {loading && !stats && <p style={{ color: "var(--muted)", textAlign: "center", padding: "1.5rem 0" }}>Chargement...</p>}

        {stats && (
          <>
            {/* ── Vue d'ensemble ── */}
            {tab === "stats" && (
              <>
                <div className="admin-stats-grid">
                  <StatCard label="Visites aujourd'hui" value={stats.overview.visits_today} />
                  <StatCard label="Visites cette semaine" value={stats.overview.visits_week} />
                  <StatCard label="Visites total" value={stats.overview.visits_total} />
                  <StatCard label="Recherches aujourd'hui" value={stats.overview.searches_today} />
                  <StatCard label="Utilisateurs" value={stats.overview.total_users} />
                  <StatCard label="Rapports total" value={stats.overview.total_reports} />
                </div>

                {stats.pass && (
                  <>
                    <h3 className="admin-section-title">Pass 24h — {stats.pass.prix_eur} €</h3>
                    <div className="admin-stats-grid">
                      <StatCard label="Pass actifs" value={stats.pass.actifs} sub="en cours à l'instant" />
                      <StatCard label="Pass accordés" value={stats.pass.accordes_total} sub={`dont ${stats.pass.expires} expirés`} />
                      <StatCard label="Clics sur le Pass" value={stats.pass.clics} sub={`${stats.pass.clics_distincts} visiteurs distincts`} />
                      <StatCard label="Emails laissés" value={stats.pass.clics_avec_email}
                        sub={stats.pass.taux_clic_email != null ? `${stats.pass.taux_clic_email} % des clics` : "—"} />
                      <StatCard label="Accès longs restants" value={stats.pass.essais_actifs} sub="anciens essais 7 j, hors Pass" />
                    </div>
                  </>
                )}

                {stats.entonnoir_7j && (
                  <>
                    <h3 className="admin-section-title">Parcours sur 7 jours</h3>
                    <div className="admin-stats-grid">
                      <StatCard label="Visites" value={stats.entonnoir_7j.visites} />
                      <StatCard label="Analyses" value={stats.entonnoir_7j.analyses}
                        sub={stats.entonnoir_7j.taux_visite_analyse != null ? `${stats.entonnoir_7j.taux_visite_analyse} % des visites` : "—"} />
                      <StatCard label="Clics Pass" value={stats.entonnoir_7j.clics_pass}
                        sub={stats.entonnoir_7j.taux_analyse_clic != null ? `${stats.entonnoir_7j.taux_analyse_clic} pour 100 analyses` : "—"} />
                      <StatCard label="Emails" value={stats.entonnoir_7j.emails_laisses}
                        sub={stats.entonnoir_7j.taux_clic_email != null ? `${stats.entonnoir_7j.taux_clic_email} % des clics` : "—"} />
                      <StatCard label="Pass accordés" value={stats.entonnoir_7j.pass_accordes} />
                    </div>
                  </>
                )}

                {stats.overview.avg_stars && (
                  <div className="admin-satisfaction-row">
                    <span className="admin-satisfaction-label">Satisfaction moyenne</span>
                    <span className="admin-satisfaction-value">
                      <Stars n={Math.round(stats.overview.avg_stars)} />
                      <span style={{ marginLeft: '0.375rem', color: "var(--gold)", fontWeight: 700 }}>
                        {stats.overview.avg_stars}/5
                      </span>
                      <span style={{ color: "var(--muted)", fontSize: '0.75rem', marginLeft: '0.25rem' }}>
                        ({stats.overview.feedback_count} avis)
                      </span>
                    </span>
                  </div>
                )}

                {stats.pending_premium.length > 0 && (
                  <div className="admin-section">
                    <h3 className="admin-section-title">
                      Demandes Premium en attente
                      <span className="admin-badge">{stats.pending_premium.length}</span>
                    </h3>
                    <div className="admin-list">
                      {stats.pending_premium.map((u) => (
                        <div key={u.id} className="admin-list-row">
                          <div>
                            <span className="admin-user-name">{u.firstname || "—"}</span>
                            <span className="admin-user-email">{u.email}</span>
                          </div>
                          <button
                            className="admin-approve-btn"
                            onClick={() => handleApprove(u.id)}
                            disabled={approving === u.id}
                          >
                            {approving === u.id ? "..." : "✓ Approuver"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="admin-section">
                  <h3 className="admin-section-title">Visites uniques — 7 derniers jours</h3>
                  <BarChart data={stats.visits_7days} />
                </div>

                <div className="admin-section">
                  <h3 className="admin-section-title">Rapports générés — 7 derniers jours</h3>
                  <BarChart data={stats.reports_7days} />
                </div>
              </>
            )}

            {/* ── Utilisateurs ── */}
            {tab === "users" && (
              <div className="admin-section" style={{ marginBottom: 0 }}>
                {selectedUserId ? (
                  <UserDetail
                    userId={selectedUserId}
                    token={token}
                    onBack={() => setSelectedUserId(null)}
                    onApprove={handleApprove}
                    approving={approving}
                  />
                ) : (
                  <>
                    <input
                      className="admin-filter-input"
                      placeholder="Filtrer par nom ou email..."
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                    />
                    {(() => {
                      const q = userFilter.toLowerCase();
                      const filtered = q
                        ? stats.users.filter(
                            (u) =>
                              u.email.toLowerCase().includes(q) ||
                              (u.firstname || "").toLowerCase().includes(q)
                          )
                        : stats.users;
                      return filtered.length === 0 ? (
                        <p className="admin-empty">Aucun résultat.</p>
                      ) : (
                        <div className="admin-list">
                          {filtered.map((u) => (
                      <div
                        key={u.id}
                        className="admin-user-row admin-user-row--clickable"
                        onClick={() => setSelectedUserId(u.id)}
                      >
                        <div className="admin-user-main">
                          <div className="admin-user-top">
                            <span className="admin-user-name">{u.firstname || "—"}</span>
                            {u.is_subscribed && <span className="admin-tag admin-tag--premium">Premium</span>}
                            {u.is_admin && <span className="admin-tag admin-tag--admin">Admin</span>}
                            {!u.is_verified && <span className="admin-tag admin-tag--muted">Non vérifié</span>}
                            {u.has_pending_premium && <span className="admin-tag admin-tag--pending">Demande Premium</span>}
                          </div>
                          <div className="admin-user-bottom">
                            <span className="admin-user-email">{u.email}</span>
                            {u.searches_today > 0 && (
                              <span className="admin-user-meta">{u.searches_today} rech. auj.</span>
                            )}
                            {u.created_at && (
                              <span className="admin-user-meta">Inscrit le {u.created_at.slice(0, 10)}</span>
                            )}
                          </div>
                        </div>
                        <span className="admin-row-arrow">›</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
                  </>
                )}
              </div>
            )}

            {/* ── Pass 24h ── */}
            {tab === "pass" && (
              <div className="admin-section" style={{ marginBottom: 0 }}>
                {!intentions ? (
                  <p className="admin-empty">Chargement des clics…</p>
                ) : intentions.length === 0 ? (
                  <p className="admin-empty">Aucun clic sur le Pass pour l'instant.</p>
                ) : (
                  <div className="admin-list">
                    {intentions.map((i) => (
                      <div key={i.id} className="admin-list-row">
                        <div>
                          <span className="admin-user-name">{i.email || "Clic sans email"}</span>
                          <div style={{ display: "flex", gap: '0.375rem', marginTop: '0.1875rem' }}>
                            {i.granted
                              ? <span className="admin-tag admin-tag--premium">Pass accordé</span>
                              : <span className="admin-tag admin-tag--muted">{i.email ? "Email seul" : "Clic seul"}</span>}
                            {i.was_logged_in && <span className="admin-tag admin-tag--muted">Connecté</span>}
                            {i.lang && <span className="admin-tag admin-tag--muted">{i.lang}</span>}
                          </div>
                        </div>
                        {i.created_at && <span className="admin-user-meta">{i.created_at.slice(0, 16).replace("T", " ")}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Leaderboard ── */}
            {tab === "leaderboard" && (
              <div className="admin-section" style={{ marginBottom: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: '1rem', padding: "0.625rem 0.875rem", background: "rgba(255,255,255,0.04)", borderRadius: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Auto-ajout au leaderboard</div>
                    <div style={{ color: "var(--muted)", fontSize: '0.75rem', marginTop: 2 }}>Chaque joueur recherché est ajouté automatiquement</div>
                  </div>
                  <button
                    onClick={handleLbToggleAutoAdd}
                    style={{ padding: "0.375rem 1rem", borderRadius: '1.25rem', border: "none", cursor: "pointer", fontWeight: 600, fontSize: '0.8125rem', background: lbAutoAdd ? "var(--gold)" : "rgba(255,255,255,0.1)", color: lbAutoAdd ? "#0d1b2a" : "var(--muted)", transition: "all 0.2s" }}
                  >
                    {lbAutoAdd ? "ON" : "OFF"}
                  </button>
                </div>
                <div style={{ display: "flex", gap: '0.5rem', marginBottom: '0.875rem' }}>
                  <input
                    className="admin-filter-input"
                    style={{ flex: 1, marginBottom: 0 }}
                    placeholder="Pseudo exact (ex: Hide on bush#KR1)"
                    value={lbInput}
                    onChange={e => setLbInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleLbAdd()}
                  />
                  <button className="admin-approve-btn" onClick={handleLbAdd} disabled={lbWorking || !lbInput.trim()}>
                    + Ajouter
                  </button>
                </div>
                {lbPlayers === null ? (
                  <p style={{ color: "var(--muted)", textAlign: "center" }}>Chargement...</p>
                ) : lbPlayers.length === 0 ? (
                  <p className="admin-empty">Aucun joueur dans le leaderboard.</p>
                ) : (
                  <div className="admin-list">
                    {lbPlayers.map((p) => (
                      <div key={p.player} className="admin-list-row">
                        <span className="admin-user-name">{p.player}</span>
                        <button
                          className="admin-approve-btn"
                          style={{ background: "rgba(255,80,80,0.15)", color: "#ff8080", borderColor: "rgba(255,80,80,0.3)" }}
                          onClick={() => handleLbRemove(p.player)}
                          disabled={lbWorking}
                        >
                          ✕ Retirer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Emails ── */}
            {tab === "emails" && (
              <EmailsTab users={stats.users} token={token} unread={unread} onInboxChange={onInboxChange} />
            )}

            {/* ── Avis ── */}
            {tab === "feedback" && (
              <div className="admin-section" style={{ marginBottom: 0 }}>
                {/* Precision des rapports — le bloc s'affiche meme a zero : masque,
                    il ne permettait pas de distinguer « personne n'a vote » d'un
                    compteur casse, et c'est ce silence qui a fait croire a une panne. */}
                <div className="admin-feedback-summary" style={{ marginBottom: '1rem', gap: '1rem' }}>
                  <span style={{ fontWeight: 700 }}>Précision des rapports</span>
                  <span style={{ color: "#4caf50" }}>✓ {stats.accuracy_yes || 0}</span>
                  <span style={{ color: "#f44336" }}>✗ {stats.accuracy_no || 0}</span>
                  {(stats.accuracy_yes + stats.accuracy_no) > 0 ? (
                    <span style={{ color: "var(--muted)", fontSize: '0.75rem' }}>
                      ({Math.round(stats.accuracy_yes / (stats.accuracy_yes + stats.accuracy_no) * 100)}% positif)
                    </span>
                  ) : (
                    <span style={{ color: "var(--muted)", fontSize: '0.75rem' }}>
                      aucun retour pour l'instant
                    </span>
                  )}
                </div>
                {stats.accuracy_recent?.length > 0 && (
                  <div className="admin-list" style={{ marginBottom: '1.5rem' }}>
                    {stats.accuracy_recent.map((a, i) => (
                      <div key={i} className="admin-feedback-row">
                        <span>{a.accurate ? "✓" : "✗"}</span>
                        {a.player && <span style={{ flex: 1, fontSize: '0.8125rem' }}>{a.player} · {a.score}/100</span>}
                        {a.created_at && <span className="admin-user-meta" style={{ flexShrink: 0 }}>{a.created_at.slice(0, 10)}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Avis étoiles */}
                {stats.feedback.length === 0 ? (
                  <p className="admin-empty">Aucun avis pour l'instant.</p>
                ) : (
                  <>
                    {stats.overview.avg_stars && (
                      <div className="admin-feedback-summary">
                        <Stars n={Math.round(stats.overview.avg_stars)} />
                        <span>{stats.overview.avg_stars}/5 · {stats.overview.feedback_count} avis</span>
                      </div>
                    )}
                    <div className="admin-list">
                      {stats.feedback.map((f, i) => (
                        <div key={i} className="admin-feedback-row">
                          <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', flex: 1 }}>
                            <Stars n={f.stars} />
                            {f.comment && (
                              <span className="admin-feedback-comment">"{f.comment}"</span>
                            )}
                          </div>
                          {f.created_at && (
                            <span className="admin-user-meta" style={{ flexShrink: 0 }}>
                              {f.created_at.slice(0, 10)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
